import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { companyInfo, navLinks, seoKeywords } from "@/data/siteContent";

const SiteLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    document
      .querySelector("meta[name='keywords']")
      ?.setAttribute("content", seoKeywords);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="site-layout-root">
      <header
        className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md"
        data-testid="site-header"
      >
        <div className="mx-auto flex w-full max-w-[1500px] items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="header-company-logo-link">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white"
              data-testid="header-company-symbol-wrapper"
            >
              <img
                src={companyInfo.headerLogo}
                alt="Keshavlal and Company symbol"
                className="h-full w-full object-contain"
                data-testid="header-company-symbol-image"
              />
            </div>

            <div className="space-y-1">
              <p
                className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl"
                data-testid="header-company-name"
              >
                {companyInfo.name}
              </p>
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600"
                data-testid="header-company-tagline"
              >
                {companyInfo.tagline}
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-4 lg:flex" data-testid="desktop-main-nav">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `text-[13px] font-semibold transition-colors hover:text-slate-950 ${
                    isActive ? "text-slate-950" : "text-slate-600"
                  }`
                }
                data-testid={`desktop-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2" data-testid="header-cta-area">
            <Button
              asChild
              className="hidden rounded-full bg-orange-500 px-6 py-5 text-sm font-semibold text-white hover:bg-orange-600 md:inline-flex"
              data-testid="header-request-quote-button"
            >
              <Link to="/contact" data-testid="header-request-quote-link">
                Request Quote
              </Link>
            </Button>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="rounded-lg border border-slate-200 p-2 text-slate-700 lg:hidden"
              data-testid="mobile-nav-toggle-button"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white p-4 lg:hidden" data-testid="mobile-nav-panel">
            <div className="grid gap-3">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className="rounded-md px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </NavLink>
              ))}
              <Button
                asChild
                className="mt-2 rounded-full bg-orange-500 py-5 font-semibold text-white hover:bg-orange-600"
                data-testid="mobile-request-quote-button"
              >
                <Link to="/contact" data-testid="mobile-request-quote-link">
                  Request Quote
                </Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main data-testid="site-main-content">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-slate-950 text-white" data-testid="site-footer">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-3 lg:px-8">
          <div className="space-y-3" data-testid="footer-company-column">
            <h3 className="text-2xl font-semibold" data-testid="footer-company-name">
              {companyInfo.name}
            </h3>
            <p className="text-sm leading-relaxed text-slate-300" data-testid="footer-company-summary">
              4th generation umbrella manufacturer delivering reliable quality and custom branding for
              corporate bulk orders across India.
            </p>
          </div>

          <div className="space-y-3" data-testid="footer-quick-links-column">
            <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-400" data-testid="footer-quick-links-title">
              Quick Links
            </h4>
            <div className="grid gap-2" data-testid="footer-quick-links-grid">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm text-slate-300 transition-colors hover:text-white"
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-3" data-testid="footer-contact-column">
            <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-400" data-testid="footer-contact-title">
              Contact
            </h4>
            <p className="text-sm text-slate-300" data-testid="footer-contact-phone">
              Phone: {companyInfo.phoneDisplay}
            </p>
            <p className="text-sm text-slate-300" data-testid="footer-contact-email">
              Email: {companyInfo.email}
            </p>
            <p className="text-sm leading-relaxed text-slate-300" data-testid="footer-contact-address">
              Address: {companyInfo.address}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;